import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";

import {
  getBiddingDetails,
  updateBidding,
} from "sr/utils/api/corporate/biddings";
import { uploadFile } from "sr/utils/api/uploadFile";

import ContentWithSpinner from "sr/layout/common/ContentWithSpiner";
import { Button } from "sr/helpers/ui-components/company/Button";
import { MainWrapper } from "../components/MainWrapper";
import { RequirementCommonHeader } from "app/pages/corporate/layout/RequirementCommonHeader";
import { ModelWrapperWithButton } from "../../component/ModelWrapperWithButton";
import { ReviewDataEdit } from "app/pages/corporate/components/ReviewDataEdit";

import BidExperienceChange from "../components/modals/BidExperienceChange";
import BidPricingTypeChange from "../components/modals/BidPricingTypeChange";
import BidAmountChange from "../components/modals/BidAmountChange";
import BidPaymentTermsChange from "../components/modals/BidPaymentTermsChange";
import { getPricingType } from "sr/utils/api/masterData/getPricingType";
import { getPaymentTerms } from "sr/utils/api/masterData/getPaymentTerms";
import BidDescriptionChange from "../components/modals/BidDescriptionChange";
import DocumentUpload from "sr/helpers/ui-components/DocumentUpload";

export const BidReview = () => {
  const intl = useIntl();
  const { id } = useParams();

  const [loader, setLoader] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);
  const [bid, setBid] = useState<any>(null);
  const [pricingOptions, setPricingOptions] = useState<{ value: string; label: string }[]>([]);
  const [paymentOptions, setPaymentOptions] = useState<{ value: string; label: string }[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [activeComponent, setActiveComponent] = useState<any>({});
  const childRef = useRef<any>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      setLoader(true);
      try {
        const res = await getBiddingDetails(id);
        if (res?.success) {
          setBid(res.data);
        } else {
          toast.error(intl.formatMessage({ id:'API.ERROR.BID.NOT_FOUND', defaultMessage:'Bid not found' }));
        }
      } catch {
        toast.error(intl.formatMessage({ id:'API.ERROR.BID.FETCH', defaultMessage:'Failed to fetch bid' }));
      }
      setLoader(false);
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    const load = async () => {
      try {
        const p = await getPricingType();
        if (p?.success && Array.isArray(p.data)) {
          setPricingOptions(p.data.map((i: any) => ({ value: i.value, label: i.label })));
        }
      } catch {}
      try {
        const t = await getPaymentTerms();
        if (t?.success && Array.isArray(t.data)) {
          setPaymentOptions(t.data.map((i: any) => ({ value: i.value, label: i.label })));
        }
      } catch {}
    };
    load();
  }, []);

  const AllModels = [
    { label: "Experience", componentName: BidExperienceChange },
    { label: "Pricing Type", componentName: BidPricingTypeChange },
    { label: "Bid Amount", componentName: BidAmountChange },
    { label: "Payment Terms", componentName: BidPaymentTermsChange },
    { label: "Description", componentName: BidDescriptionChange },
  ];

  const openModel = (index: number) => {
    setActiveComponent(AllModels[index]);
    setShowModal(true);
  };

  const HandleSave = async () => {
    if (!childRef.current?.submitForm) return;
    if (typeof childRef.current.validateData === 'function') {
      const ok = await childRef.current.validateData();
      if (!ok) return;
    }
    const newData = await childRef.current.submitForm();
    const payload = { ...bid, ...newData };

    setLoader(true);
    try {
      const result = await updateBidding(bid._id || bid.id, payload);
      if (result?.success) {
        setBid(payload);
        setShowModal(false);
      }
    } catch {
      toast.error(intl.formatMessage({ id:'API.ERROR.BID.UPDATE', defaultMessage:'Failed to update bid' }));
    } finally {
      setLoader(false);
    }
  };

  const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !id) return;

    setUploading(true);
    try {
      const uploaded: any[] = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append("task_img", file);

        const res = await uploadFile(formData);
        if (res?.success && res.data?.files?.[0]?.location) {
          uploaded.push({
            url: res.data.files[0].location,
            name: file.name,
          });
        }
      }

      const updatedDocs = [...(bid.documents || []), ...uploaded];
      const payload = { ...bid, documents: updatedDocs };
      const result = await updateBidding(id, payload);

      if (result?.success) {
        setBid(payload);
        toast.success(intl.formatMessage({ id:'API.SUCCESS.BID.UPLOAD', defaultMessage:'File(s) uploaded successfully' }));
      }
    } catch {
      toast.error(intl.formatMessage({ id:'API.ERROR.BID.UPLOAD', defaultMessage:'Failed to upload documents' }));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleAttachmentRemove = async (index: number) => {
    if (!id) return;
    setRemovingIndex(index);

    const updated = (bid.documents || []).filter((_: any, i: number) => i !== index);
    const payload = { ...bid, documents: updated };

    try {
      const result = await updateBidding(id, payload);
      if (result?.success) {
        setBid(payload);
        toast.success("File removed successfully");
      }
    } catch {
      toast.error(intl.formatMessage({ id:'API.ERROR.BID.REMOVE_FILE', defaultMessage:'Failed to remove file' }));
    } finally {
      setRemovingIndex(null);
    }
  };

  return (
    <MainWrapper>
      <ContentWithSpinner loader={loader} height="h-screen">
        {bid && (
          <div className="container mx-auto max-w-6xl py-8 px-4">
            <RequirementCommonHeader
              title={
                <FormattedMessage
                  id="COMPANY.BID.REVIEW.TITLE"
                  defaultMessage="Review Bid"
                />
              }
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <ReviewDataEdit title="Experience" value={bid.experience} onClick={() => openModel(0)} />
              <ReviewDataEdit title="Pricing Type" value={bid.pricing_type} options={pricingOptions} onClick={() => openModel(1)} />
              <ReviewDataEdit title="Bid Amount" value={bid.bid_amount} onClick={() => openModel(2)} />
              <ReviewDataEdit title="Payment Terms" value={bid.payment_terms} options={paymentOptions} onClick={() => openModel(3)} />
              <ReviewDataEdit title="Description" value={bid.description} onClick={() => openModel(4)} />
            </div>

            <div className="mt-6">
              <DocumentUpload
                documents={bid.documents || []}
                onUpload={handleAttachmentUpload}
                onRemove={handleAttachmentRemove}
                uploading={uploading}
                removingIndex={removingIndex}
              />
            </div>

            <div className="flex justify-end mt-6">
              <Button
                text={<FormattedMessage id="COMMON.BACK" defaultMessage="Back to Projects" />}
                onClick={() => navigate("/projects")}
              />
            </div>
          </div>
        )}
      </ContentWithSpinner>

      <ModelWrapperWithButton
        Save={HandleSave}
        showModal={showModal}
        onCheckModel={setShowModal}
        headertitle="Edit Bid"
      >
        {activeComponent?.componentName &&
          React.createElement(activeComponent.componentName, {
            data: bid,
            HandleDataProps: () => {},
            ref: childRef,
          })}
      </ModelWrapperWithButton>
    </MainWrapper>
  );
};

export default BidReview;
